import { useMemo } from 'react';
import {
  getManagerAssigneeOptions,
  getNextManagerSortState,
} from '~/modules/covenants/CovenantsDataForm/CovenantsFormByManager/utils/utils.ts';
import type {
  TManagerApplicationAssigneeFilter,
  TManagerApplicationSortKey,
  TManagerApplicationStatusFilter,
  TUseManagerApplicationsTableParams,
} from '~/modules/covenants/CovenantsDataForm/CovenantsFormByManager/types.ts';

const areOrderedStringArraysEqual = (left: string[], right: string[]) =>
  left.length === right.length && left.every((value, index) => value === right[index]);

export const useManagerApplicationsTable = ({
  applications,
  filters,
  onFiltersChange,
}: TUseManagerApplicationsTableParams) => {
  const assigneeOptions = useMemo(() => getManagerAssigneeOptions(applications), [applications]);

  const handleStatusFilterChange = (status: TManagerApplicationStatusFilter) => {
    onFiltersChange((prevState) => {
      if (areOrderedStringArraysEqual(prevState.status, status) && prevState.page === 0) {
        return prevState;
      }

      return {
        ...prevState,
        status,
        page: 0,
      };
    });
  };

  const handleAssigneeFilterChange = (assignee: TManagerApplicationAssigneeFilter) => {
    onFiltersChange((prevState) => {
      if (areOrderedStringArraysEqual(prevState.assignee, assignee) && prevState.page === 0) {
        return prevState;
      }

      return {
        ...prevState,
        assignee,
        page: 0,
      };
    });
  };

  const handleSegmentFilterChange = (segmentIds: string[]) => {
    onFiltersChange((prevState) => {
      if (
        areOrderedStringArraysEqual(prevState.segmentIds, segmentIds) &&
        prevState.page === 0
      ) {
        return prevState;
      }

      return {
        ...prevState,
        segmentIds,
        page: 0,
      };
    });
  };

  const handleSortToggle = (sortKey: TManagerApplicationSortKey) => {
    onFiltersChange((prevState) => ({
      ...prevState,
      ...getNextManagerSortState(prevState.sortKey, prevState.sortDirection, sortKey),
      page: 0,
    }));
  };

  const handlePageChange = (_event: unknown, nextPage: number) => {
    onFiltersChange((prevState) => {
      if (prevState.page === nextPage) {
        return prevState;
      }

      return {
        ...prevState,
        page: nextPage,
      };
    });
  };

  const handleRowsPerPageChange = (nextRowsPerPage: number) => {
    onFiltersChange((prevState) => {
      if (prevState.rowsPerPage === nextRowsPerPage && prevState.page === 0) {
        return prevState;
      }

      return {
        ...prevState,
        rowsPerPage: nextRowsPerPage,
        page: 0,
      };
    });
  };

  return {
    filters,
    assigneeOptions,
    isStatusFilterActive: !!filters.status.length,
    isAssigneeFilterActive: !!filters.assignee.length,
    isSegmentFilterActive: !!filters.segmentIds.length,
    handleStatusFilterChange,
    handleAssigneeFilterChange,
    handleSegmentFilterChange,
    handleSortToggle,
    handlePageChange,
    handleRowsPerPageChange,
  };
};
